import { apiDelete, apiGet, apiPost, apiPut, apiUpload } from './api-client'
import type {
  AccountType,
  Bill,
  BillStatus,
  BusinessEntity,
  CpaShare,
  DocumentMeta,
  EntityClassification,
  EntityInput,
  EntityPnL,
  EntityRecord,
  EntityRecordInput,
  FinanceDocument,
  FinanceSummary,
  FinancialAccount,
  Form1099Input,
  Form1099Record,
  Holding,
  Institution,
  ManualAccountInput,
  MonthlyBudget,
  PaymentTerms,
  PnLLine,
  Property,
  PropertyInput,
  ReturnScheduleItem,
  ReturnStatus,
} from '../types/family-finance'
import type { AccountUse } from '../types/family-finance'
import {
  applyClassification,
  clearClassification,
  setClassification,
} from './account-classification'
import { deleteBlob, getBlobUrl, MAX_FILE_BYTES, putBlob } from './document-blob-store'
import type { TaxEstimateInputs } from './tax-estimator'

// ─── Live data only ──────────────────────────────────────────
// This module talks to the deployed family-finance service and nothing else.
// There is no sample dataset and no fallback: for money, a placeholder balance
// that looks real is worse than an error, so an unreachable or unconfigured API
// surfaces as a failure the UI reports.

const FAMILY_API_URL = import.meta.env.VITE_FAMILY_API_URL || ''
export const FINANCE_API_CONFIGURED = !!FAMILY_API_URL

export class FinanceApiNotConfiguredError extends Error {
  constructor() {
    super(
      'The Finances API is not configured for this build. Set VITE_FAMILY_API_URL to the deployed family-finance API Gateway URL.',
    )
    this.name = 'FinanceApiNotConfiguredError'
  }
}

// Earlier builds cached a seeded demo portfolio in localStorage. Nothing reads
// it now, but fabricated balances shouldn't linger in the browser either.
try {
  localStorage.removeItem('nb-family-mock-v2')
} catch {
  // storage unavailable — nothing to clean up
}

/** Every call goes through here, so a missing base URL fails the same way everywhere. */
async function live<T>(request: () => Promise<T>): Promise<T> {
  if (!FINANCE_API_CONFIGURED) throw new FinanceApiNotConfiguredError()
  return request()
}

// ─── Date helpers ────────────────────────────────────────────

function fmt(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function daysFromNow(days: number): string {
  return fmt(new Date(Date.now() + days * 86400000))
}

export function currentMonth(): string {
  return fmt(new Date()).slice(0, 7)
}

export function billStatusOf(bill: Bill): BillStatus {
  if (bill.frequency === 'one-time' && bill.lastPaid) return 'paid'
  const today = fmt(new Date())
  if (bill.nextDueDate < today) return 'overdue'
  if (bill.nextDueDate <= daysFromNow(7)) return 'due-soon'
  return 'upcoming'
}

/** Zeroed starting point — the owner's real figures come from the service. */
function emptyEstimatorInputs(): TaxEstimateInputs {
  return {
    filingStatus: 'mfj',
    wages: 0,
    federalWithholding: 0,
    seNetIncome: 0,
    otherIncome: 0,
    preTaxContributions: 0,
    itemizedDeductions: 0,
    dependentsUnder17: 0,
    estimatedPaymentsMade: 0,
  }
}

// ─── Summary ─────────────────────────────────────────────────

export async function getFinanceSummary(): Promise<FinanceSummary> {
  return live(() => apiGet<FinanceSummary>('/api/family/summary'))
}

// ─── Institutions & accounts ─────────────────────────────────

export async function listInstitutions(): Promise<Institution[]> {
  return live(() => apiGet<Institution[]>('/api/family/institutions'))
}

export async function listAccounts(): Promise<FinancialAccount[]> {
  return live(() => apiGet<FinancialAccount[]>('/api/family/accounts').then(applyClassification))
}

/**
 * Record whether an account is personal or business (and, for business, which
 * entity's books it belongs to). Stored client-side — see account-classification
 * for why — so this succeeds whether or not the backend is reachable.
 */
export async function setAccountUse(
  id: string,
  use: AccountUse,
  entityId: string | null,
): Promise<void> {
  setClassification(id, { use, entityId })
}

// SimpleFIN: the owner links banks at bridge.simplefin.org and pastes a
// one-time setup token here; the backend claims it and syncs balances.
export interface SimpleFinClaimResult {
  institutions: number
  accounts: number
  /** False only on older backends that predate the field. */
  linked?: boolean
  /** The link is established but the first balance pull hasn't finished. */
  syncPending?: boolean
}

/** What a full re-pull found at the bridge — institutions and accounts now authorized. */
export interface SimpleFinSyncResult {
  institutions: number
  accounts: number
}

export async function claimSimpleFin(setupToken: string): Promise<SimpleFinClaimResult> {
  // Base64 never contains whitespace, but a token copied out of the bridge page
  // or an email often arrives wrapped across lines. The backend only trims the
  // ends, and Python's strict base64 decode rejects an embedded newline — so
  // strip every space before it goes over the wire.
  const token = setupToken.replace(/\s+/g, '')
  if (!token) throw new Error('setupToken is required')
  return live(() =>
    apiPost<SimpleFinClaimResult>('/api/family/connections/simplefin/claim', { setupToken: token }),
  )
}

/**
 * Re-pull everything the SimpleFIN access URL currently authorizes.
 *
 * This is the one that picks up banks or accounts added at the bridge *after*
 * the original claim: the access URL is permanent and the backend upserts every
 * account the bridge returns, so newly shared ones arrive as new institutions
 * and rows. Per-institution "Sync now" already does the same full pull, but
 * there is no card to press when the new institution isn't in the app yet.
 */
export async function syncSimpleFin(): Promise<SimpleFinSyncResult> {
  return live(() => apiPost<SimpleFinSyncResult>('/api/family/connections/simplefin/sync'))
}

export async function addManualAccount(input: ManualAccountInput): Promise<FinancialAccount> {
  return live(async () => {
    const result = await apiPost<FinancialAccount>('/api/family/accounts', input)
    // The service doesn't store `use`, so keep the owner's choice locally
    // against the id it just assigned.
    setClassification(result.id, { use: input.use, entityId: input.entityId })
    return { ...result, use: input.use, entityId: input.entityId }
  })
}

/**
 * Correct what kind of account this is.
 *
 * SimpleFIN carries no account-type field, so the service infers one from the
 * account name — which lands a Schwab account named "Individual …" in Checking
 * and keeps it out of the Investments view. The service *does* store `type`,
 * and its sync preserves an existing value, so this correction is permanent.
 */
export async function setAccountType(id: string, type: AccountType): Promise<FinancialAccount> {
  return live(() =>
    apiPut<FinancialAccount>(`/api/family/accounts/${encodeURIComponent(id)}`, { type }),
  )
}

/**
 * Record what a card or loan costs each month.
 *
 * SimpleFIN authorizes balances only and almost never carries a minimum
 * payment or a due date, so these are the owner's figures. Stored on the
 * account (not just on the bill) so they survive syncs and so the bill can be
 * regenerated from them.
 */
export async function setPaymentTerms(id: string, terms: PaymentTerms): Promise<FinancialAccount> {
  return live(() =>
    apiPut<FinancialAccount>(`/api/family/accounts/${encodeURIComponent(id)}`, terms),
  )
}

export async function deleteAccount(id: string): Promise<void> {
  return live(async () => {
    await apiDelete<void>(`/api/family/accounts/${encodeURIComponent(id)}`)
    clearClassification(id)
  })
}

export async function syncInstitution(id: string): Promise<Institution> {
  return live(() => apiPost<Institution>(`/api/family/institutions/${encodeURIComponent(id)}/sync`))
}

export async function disconnectInstitution(id: string): Promise<void> {
  return live(() => apiPost<void>(`/api/family/institutions/${encodeURIComponent(id)}/disconnect`))
}

// ─── Investments ─────────────────────────────────────────────

export async function listHoldings(): Promise<Holding[]> {
  return live(() => apiGet<Holding[]>('/api/family/holdings'))
}

// ─── Properties ──────────────────────────────────────────────
// One address in, facts and a value out. The lookups run server-side because
// both upstreams are bearer-key APIs the browser must never hold a key for:
// the Easy Tax Protest CAD database (physical facts) and the Ironrock
// Properties partner CMA (what comparable houses sold for).

export async function listProperties(): Promise<Property[]> {
  return live(() =>
    apiGet<Property[]>('/api/family/properties').then((rows) =>
      [...rows].sort((a, b) => (a.label || a.address).localeCompare(b.label || b.address)),
    ),
  )
}

/**
 * Create and enrich in one call — the address *is* the interaction, so the
 * service runs both lookups before it answers. That makes this request as slow
 * as a CMA (a few seconds), which is why callers show a pending state.
 */
export async function addProperty(input: PropertyInput): Promise<Property> {
  const address = input.address.trim()
  if (!address) throw new Error('A street address is required.')
  return live(() => apiPost<Property>('/api/family/properties', { ...input, address }))
}

export async function updateProperty(id: string, patch: Partial<PropertyInput>): Promise<Property> {
  return live(() => apiPut<Property>(`/api/family/properties/${encodeURIComponent(id)}`, patch))
}

export async function deleteProperty(id: string): Promise<void> {
  return live(() => apiDelete<void>(`/api/family/properties/${encodeURIComponent(id)}`))
}

/** Re-run every lookup. A source that fails leaves its prior value in place. */
export async function refreshProperty(id: string): Promise<Property> {
  return live(() => apiPost<Property>(`/api/family/properties/${encodeURIComponent(id)}/refresh`))
}

/**
 * Fast load: resolve an address against every source WITHOUT saving it.
 *
 * Backs the address box — the owner sees the photo, the county's record of the
 * house, and each value that answered before deciding to keep the property.
 * Same server code as the real enrichment, so the preview is not a different
 * answer from what gets stored.
 */
export async function lookupProperty(input: {
  address: string
  city?: string
  zip?: string
  livingAreaSqft?: number | null
}): Promise<Partial<Property>> {
  const address = input.address.trim()
  if (!address) throw new Error('A street address is required.')
  return live(() => apiPost<Partial<Property>>('/api/family/properties/lookup', { ...input, address }))
}

// ─── Budget ──────────────────────────────────────────────────

export async function getBudget(): Promise<MonthlyBudget> {
  return live(() => apiGet<MonthlyBudget>('/api/family/budget'))
}

// ─── Bills ───────────────────────────────────────────────────

export async function listBills(): Promise<Bill[]> {
  return live(() =>
    apiGet<Bill[]>('/api/family/bills').then((bills) =>
      [...bills].sort((a, b) => a.nextDueDate.localeCompare(b.nextDueDate)),
    ),
  )
}

export async function addBill(input: Omit<Bill, 'id' | 'lastPaid'>): Promise<Bill> {
  return live(() => apiPost<Bill>('/api/family/bills', input))
}

export async function updateBill(id: string, patch: Partial<Bill>): Promise<Bill> {
  return live(() => apiPut<Bill>(`/api/family/bills/${encodeURIComponent(id)}`, patch))
}

export async function markBillPaid(id: string): Promise<Bill> {
  return live(() => apiPost<Bill>(`/api/family/bills/${encodeURIComponent(id)}/paid`))
}

// ─── Documents ───────────────────────────────────────────────

export async function listDocuments(): Promise<FinanceDocument[]> {
  return live(() =>
    apiGet<FinanceDocument[]>('/api/family/documents').then((docs) =>
      [...docs].sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt)),
    ),
  )
}

/**
 * Commit one upload. The bytes go up as multipart and errors surface honestly —
 * silently succeeding would tell the owner a document was filed when nothing was
 * stored.
 */
export async function uploadDocument(file: File, meta: DocumentMeta): Promise<FinanceDocument> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is ${MAX_FILE_BYTES / 1048576} MB.`,
    )
  }
  return live(async () => {
    const form = new FormData()
    form.append('file', file, file.name)
    form.append('meta', JSON.stringify(meta))
    const document = await apiUpload<FinanceDocument>('/api/family/documents', form)
    // Keep a local copy of the bytes so previews work without a second
    // round-trip; harmless if the browser refuses the write.
    await putBlob(document.id, file).catch(() => false)
    return document
  })
}

export async function updateDocument(
  id: string,
  patch: Partial<DocumentMeta> & { sharedWithCpa?: boolean },
): Promise<FinanceDocument> {
  return live(() => apiPut<FinanceDocument>(`/api/family/documents/${encodeURIComponent(id)}`, patch))
}

export async function deleteDocument(id: string): Promise<void> {
  return live(async () => {
    await apiDelete<void>(`/api/family/documents/${encodeURIComponent(id)}`)
    await deleteBlob(id)
  })
}

/** Object URL for previewing or downloading a document, or null if the bytes aren't stored. */
export async function getDocumentUrl(doc: FinanceDocument): Promise<string | null> {
  if (!doc.hasFile) return null
  if (doc.storageKey) {
    const { url } = await apiGet<{ url: string }>(
      `/api/family/documents/${encodeURIComponent(doc.id)}/url`,
    )
    return url
  }
  // Uploaded from this browser and not yet mirrored server-side.
  return getBlobUrl(doc.id)
}

export async function setDocumentShared(id: string, shared: boolean): Promise<FinanceDocument> {
  return updateDocument(id, { sharedWithCpa: shared })
}

// ─── CPA sharing ─────────────────────────────────────────────

export async function listCpaShares(): Promise<CpaShare[]> {
  return live(() => apiGet<CpaShare[]>('/api/family/tax/cpa-shares'))
}

export async function inviteCpa(req: { email: string; name: string; taxYears: number[] }): Promise<CpaShare> {
  return live(() => apiPost<CpaShare>('/api/family/tax/cpa-shares', req))
}

export async function revokeCpaShare(id: string): Promise<void> {
  return live(() => apiPost<void>(`/api/family/tax/cpa-shares/${encodeURIComponent(id)}/revoke`))
}

// ─── Estimator inputs (persisted) ────────────────────────────

export async function getEstimatorInputs(): Promise<TaxEstimateInputs> {
  return live(() =>
    apiGet<Partial<TaxEstimateInputs>>('/api/family/tax/estimator').then((r) => ({
      // Backend returns {} until inputs are saved; never let missing fields
      // reach the estimator math.
      ...emptyEstimatorInputs(),
      ...(r && typeof r.filingStatus === 'string' ? r : {}),
    })),
  )
}

export async function saveEstimatorInputs(inputs: TaxEstimateInputs): Promise<void> {
  return live(() => apiPut<void>('/api/family/tax/estimator', inputs))
}

// ─── Entities, P&L, 1099s, return schedule ───────────────────

export async function listEntities(): Promise<BusinessEntity[]> {
  return live(() => apiGet<BusinessEntity[]>('/api/family/entities'))
}

export async function createEntity(input: EntityInput): Promise<BusinessEntity> {
  return live(() => apiPost<BusinessEntity>('/api/family/entities', input))
}

export async function updateEntity(id: string, patch: Partial<EntityInput>): Promise<BusinessEntity> {
  return live(() => apiPut<BusinessEntity>(`/api/family/entities/${encodeURIComponent(id)}`, patch))
}

export async function updateEntityClassification(
  id: string,
  classification: EntityClassification,
): Promise<BusinessEntity> {
  return updateEntity(id, { classification })
}

/** Removes the entity and its P&Ls, 1099s, and records. Documents are kept but unlinked. */
export async function deleteEntity(id: string): Promise<void> {
  return live(() => apiDelete<void>(`/api/family/entities/${encodeURIComponent(id)}`))
}

export async function listPnL(): Promise<EntityPnL[]> {
  return live(() => apiGet<EntityPnL[]>('/api/family/entities/pnl'))
}

/** Upsert by (entityId, year). Net income is always derived, never entered. */
export async function savePnL(input: {
  entityId: string
  year: number
  through: string
  revenue: number
  expenses: PnLLine[]
}): Promise<EntityPnL> {
  const expenses = input.expenses.filter((e) => e.category.trim() !== '')
  const pnl: EntityPnL = {
    ...input,
    expenses,
    netIncome: input.revenue - expenses.reduce((sum, e) => sum + e.amount, 0),
  }
  return live(() => apiPut<EntityPnL>('/api/family/entities/pnl', pnl))
}

export async function deletePnL(entityId: string, year: number): Promise<void> {
  return live(() => apiDelete<void>(`/api/family/entities/${encodeURIComponent(entityId)}/pnl/${year}`))
}

export async function list1099s(): Promise<Form1099Record[]> {
  return live(() => apiGet<Form1099Record[]>('/api/family/entities/1099s'))
}

export async function add1099(input: Form1099Input): Promise<Form1099Record> {
  return live(() => apiPost<Form1099Record>('/api/family/entities/1099s', input))
}

export async function update1099(id: string, patch: Partial<Form1099Input>): Promise<Form1099Record> {
  return live(() => apiPut<Form1099Record>(`/api/family/entities/1099s/${encodeURIComponent(id)}`, patch))
}

export async function delete1099(id: string): Promise<void> {
  return live(() => apiDelete<void>(`/api/family/entities/1099s/${encodeURIComponent(id)}`))
}

// ─── Entity records ──────────────────────────────────────────

export async function listEntityRecords(): Promise<EntityRecord[]> {
  return live(() =>
    apiGet<EntityRecord[]>('/api/family/entities/records').then((records) =>
      [...records].sort((a, b) => b.date.localeCompare(a.date)),
    ),
  )
}

export async function addEntityRecord(input: EntityRecordInput): Promise<EntityRecord> {
  return live(() => apiPost<EntityRecord>('/api/family/entities/records', input))
}

export async function updateEntityRecord(
  id: string,
  patch: Partial<EntityRecordInput>,
): Promise<EntityRecord> {
  return live(() => apiPut<EntityRecord>(`/api/family/entities/records/${encodeURIComponent(id)}`, patch))
}

export async function deleteEntityRecord(id: string): Promise<void> {
  return live(() => apiDelete<void>(`/api/family/entities/records/${encodeURIComponent(id)}`))
}

export async function listReturnSchedule(): Promise<ReturnScheduleItem[]> {
  return live(() => apiGet<ReturnScheduleItem[]>('/api/family/tax/returns'))
}

export async function updateReturnStatus(id: string, status: ReturnStatus): Promise<ReturnScheduleItem> {
  return live(() =>
    apiPut<ReturnScheduleItem>(`/api/family/tax/returns/${encodeURIComponent(id)}`, { status }),
  )
}
